import type { InputRouter, LayerHandle } from "../../ui/input/router.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import { actionPress } from "../controls/press.ts";
import type { WindowOrder } from "../windows/order.ts";
import type { CharacterStore } from "./store.ts";

/**
 * The character window's key (ADR-138's amendment of 2026-10-31, decision 1; P1.9-T220): the key the page's table binds
 * Character to, C by default, opens and closes the window while the game owns the keyboard. While open the window is a
 * layer of the input router that names no key, so Escape closes it as the topmost layer and every other key reaches
 * the game, as the bag's does (`bag/keys.ts`). An open dialog closes before the window. With a second play window the
 * two share an order and the window raised last takes Escape first.
 */

/**
 * Binds the window's key to the page's input router and the store.
 *
 * @param store The character store, whose open state the layer follows.
 * @param input The page's input router.
 * @param order The play windows' order, or undefined on a page with one play window.
 * @param keys The key table; the page's unless given.
 * @returns A function that unbinds the key and closes the window's layer.
 * @example
 * bindCharacterKeys(character, input, windowOrder);
 */
export function bindCharacterKeys(store: Pick<CharacterStore, "subscribe" | "getSnapshot" | "setOpen" | "toggle" | "ask">, input: Pick<InputRouter, "open" | "onKey">, order?: Pick<WindowOrder, "subscribe" | "raise" | "front">, keys: Pick<KeyTable, "actionOf"> = pageKeys): () => void {
  let layer: LayerHandle | null = null;

  /**
   * Opens the window's layer: Escape closes an open dialog first, then the window.
   */
  const openLayer = (): void => {
    layer = input.open({
      onClose: () => {
        if (layer === null) {
          return;
        }
        layer = null;
        if (store.getSnapshot().dialog !== "none") {
          openLayer();
          store.ask("none");
        } else {
          store.setOpen(false);
        }
      },
    });
  };

  /**
   * Makes the layer follow the store's open state.
   */
  const follow = (): void => {
    const { open } = store.getSnapshot();
    if (open && layer === null) {
      openLayer();
      order?.raise("character");
    } else if (!open && layer !== null) {
      const opened = layer;
      layer = null;
      opened.close();
    }
  };

  /**
   * Makes the layer the router's topmost when the window was raised over another.
   */
  const raised = (): void => {
    if (layer !== null && order?.front("character") === true) {
      const opened = layer;
      layer = null;
      opened.close();
      openLayer();
    }
  };
  const unsubscribe = store.subscribe(follow);
  const unorder = order?.subscribe(raised);
  const unlisten = input.onKey((event) => {
    if (event.repeat || event.defaultPrevented || !actionPress(keys, "characterSheet", event) || event.ctrlKey === true || event.altKey === true || event.metaKey === true) {
      return;
    }
    event.preventDefault();
    store.toggle();
  });
  follow();
  return () => {
    unsubscribe();
    unorder?.();
    unlisten();
    const opened = layer;
    layer = null;
    opened?.close();
  };
}
